'use client';

import SortMenu from '@/components/SortMenu';
import { useStore } from '@/providers/app-store-provider';
import { setSession } from '@/utils/tools';
import { WapFilterOutlined, WapLayoutMoreOutlined, WapLayoutSimpleOutlined } from '@zeelool/icon';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { DEFAULT_SORT_VALUE } from '@/utils/constants';
import { useTranslations } from '@/i18n/use-translations';

interface SortFilterBarProps {
  currentSort: string;
  onSortChange: (sortValue: { value?: string | number; sortName?: string }) => void;

  // 筛选相关
  filterTotal?: number;
  onFilterClick: () => void;

  // 列数切换相关
  showColumnControl?: boolean;
  column?: number;
  onColumnChange?: (column: number) => void;

  // 其他配置
  isAcc?: boolean; // 是否为饰品页面
  groupInfo?: any; // 分组信息
  listId?: number | string;
  isNewin?: boolean;
  isWeb?: boolean;
  inAppH5?: boolean;
  total?: number;
  noData?: boolean;
  wrapperClassName?: string;
  onSortBtnClick?: () => void;
  /** 与筛选抽屉 open 状态同步，用于 aria-expanded / aria-controls */
  filterDrawerOpen?: boolean;
  filterDrawerContentId?: string;
}

export default function SortFilterBar({
  currentSort,
  onSortChange,
  filterTotal = 0,
  onFilterClick,
  showColumnControl = true,
  column = 2,
  onColumnChange,
  isAcc = false,
  groupInfo = {},
  listId,
  isNewin,
  isWeb,
  inAppH5,
  total,
  noData,
  wrapperClassName = '',
  onSortBtnClick,
  filterDrawerOpen = false,
  filterDrawerContentId,
}: SortFilterBarProps) {
  const swiperRef = useRef<any>(null);
  const t = useTranslations('goodsList.sortFilterBar');
  const publicResourceData = useStore((state) => state.publicResourceData);

  const product_sort_new = useMemo(() => {
    try {
      return publicResourceData.config_names.product_sort_new;
    } catch {
      return [];
    }
  }, [publicResourceData]);

  const productGroupSort = useMemo(() => {
    const { page_template: { product_group_sort: _productGroupSort = [] } = {} } = groupInfo;
    if (!_productGroupSort || _productGroupSort.length === 0) {
      return [];
    }
    return _productGroupSort.filter((item: any) => item.is_show);
  }, [groupInfo]);

  // 当 listId 变化时，滚动到对应的 item
  useEffect(() => {
    if (listId && swiperRef.current && productGroupSort.length > 0) {
      // 找到当前 listId 在 productGroupSort 中的索引
      const currentIndex = productGroupSort.findIndex((item: any) => item.id === groupInfo.id);
      if (currentIndex !== -1) {
        // 滚动到对应的 slide
        swiperRef.current.slideTo(currentIndex);
      }
    }
  }, [listId, productGroupSort, groupInfo]);

  const sortOptions = useMemo(() => {
    return product_sort_new.map((item) => {
      return {
        label: item.name,
        value: item.value || DEFAULT_SORT_VALUE,
      };
    });
  }, [product_sort_new]);

  // 处理列数切换
  const handleColumnChange = useCallback(() => {
    const newColumn = column === 1 ? 2 : 1;
    onColumnChange?.(newColumn);
  }, [column, onColumnChange]);

  // 处理排序变化
  const handleSortChange = (value: string | number) => {
    const sortName =
      sortOptions.find((option) => option.value === value)?.label || DEFAULT_SORT_VALUE;
    onSortChange({
      value: value === DEFAULT_SORT_VALUE ? '' : value,
      sortName,
    });
  };

  // Swiper配置
  const swiperOptions = useMemo(
    () => ({
      slidesOffsetBefore: 13,
      slidesOffsetAfter: 13,
      slidesPerView: (isWeb ? 3.5 : 'auto') as number | 'auto',
      spaceBetween: 10,
    }),
    [isWeb],
  );

  // 如果不显示筛选功能，则不渲染
  if (!groupInfo.filter_show) {
    return null;
  }

  if (noData) {
    return null;
  }

  // 计算顶部位置类名
  const getTopClassName = () => {
    if (isWeb) return 'top-[100px]';
    if (inAppH5) return 'top-[0px]';
    return 'border-b border-gray-200 top-[11.733vw]';
  };

  return (
    <div
      className={`sort-filter-bar sticky bg-white z-[3] ${getTopClassName()} ${wrapperClassName}`}
    >
      {/* 新品分类滑块 */}
      {isNewin && productGroupSort.length > 0 && !isWeb && (
        <div className="new-in-sort pt-16 pb-20">
          <Swiper
            className="second-nav-ul"
            slidesOffsetBefore={swiperOptions.slidesOffsetBefore}
            slidesOffsetAfter={swiperOptions.slidesOffsetAfter}
            slidesPerView={swiperOptions.slidesPerView}
            spaceBetween={swiperOptions.spaceBetween}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
          >
            {productGroupSort.map((item: any) => {
              // if (!item.new_in_date) return null;

              return (
                <SwiperSlide
                  key={item.id}
                  className="second-nav-li"
                  style={{ width: isWeb ? '100px' : 'auto' }}
                >
                  <Link
                    href={`/goods-list/${item.path || item.id}`}
                    className={`
                     second-nav-li-a h-[28px] rounded-[14px] text-12  flex items-center justify-center border-1
                     ${groupInfo.id === item.id ? 'active bg-primary-text text-white' : 'border-primary-text bg-white'}
                     ${isWeb ? '' : 'px-10'}
                   `}
                    onClick={() =>
                      setSession('zl_goodsListEnterPageScene', '商品列表页新品分组导航位')
                    }
                  >
                    {item.new_in_text.toLowerCase() === 'new in' ? t('newIn') : item.new_in_text}{' '}
                    {item.transverse_new_in_date}
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      )}

      <div
        className={`flex items-center ${isWeb ? 'pt-20 pb-14 flex-row-reverse' : 'px-[14px] h-[36px] justify-between'}`}
      >
        {/* 排序菜单 */}
        <div className={`flex items-center ${isWeb ? 'min-[920px]:ml-auto' : ''}`}>
          <SortMenu
            options={sortOptions}
            value={currentSort === '' ? DEFAULT_SORT_VALUE : currentSort}
            onChange={handleSortChange}
            onSortBtnClick={onSortBtnClick}
            buttonStyle={{
              padding: '8px 4px 8px 0',
              minWidth: 'auto',
              fontSize: '14px',
              fontWeight: 'normal',
            }}
            menuSlotProps={{
              paper: {
                sx: {
                  right: '40px',
                  left: 'unset !important',
                },
              },
              list: {
                sx: {
                  '& .MuiMenuItem-root:hover': {
                    backgroundColor: '#EDECFD !important',
                  },
                },
              },
            }}
          />
        </div>

        {/* 右侧控制区域 */}
        <div className={`flex items-center ${isWeb ? 'ml-auto mr-36 min-[920px]:hidden' : ''}`}>
          {/* 筛选按钮 */}
          {!isAcc && groupInfo.filter_show === 1 && (
            <button
              type="button"
              aria-label={t('openFilters')}
              aria-expanded={filterDrawerOpen}
              {...(filterDrawerContentId ? { 'aria-controls': filterDrawerContentId } : {})}
              aria-haspopup="dialog"
              className={`flex items-center cursor-pointer font-bb md:font-br p-0 m-0 border-0 bg-transparent`}
              onClick={onFilterClick}
            >
              <span className={`text-14  ${isWeb ? 'font-bb' : ''}`}>
                {isWeb ? t('filters') : t('filter')}
              </span>
              {filterTotal > 0 && (
                <span
                  className={`block bg-primary-purple text-white text-center  ${isWeb ? 'min-w-20 h-20 rounded-10 text-12 leading-[20px] ml-[5px]' : 'min-w-14 h-14 rounded-[7px] text-[10px] leading-[14px] ml-[3px]'}`}
                  aria-hidden
                >
                  {filterTotal}
                </span>
              )}
              <WapFilterOutlined className="ml-[8px] text-[20px] text-[#282828]" aria-hidden />
            </button>
          )}

          {/* 列数切换控制 */}
          {showColumnControl && !isAcc && !isWeb && (
            <div className="flex items-center pl-[10px] ml-[13px] border-l border-[#eeeeee]">
              <button
                type="button"
                className="bg-transparent border-0 p-0 cursor-pointer flex items-center justify-center"
                onClick={handleColumnChange}
                aria-label={
                  column === 1 ? 'Switch to two columns view' : 'Switch to single column view'
                }
                title={column === 1 ? 'Switch to two columns view' : 'Switch to single column view'}
              >
                {column === 1 ? (
                  // 单列图标
                  <WapLayoutSimpleOutlined className="text-[20px] text-[#282828]" />
                ) : (
                  // 双列图标
                  <WapLayoutMoreOutlined className="text-[20px] text-[#282828]" />
                )}
              </button>
            </div>
          )}
        </div>
        {isWeb && (
          <div className="text-14 leading-[20px]">
            {t('showing', { total, result: total > 1 ? t('results') : t('result') })}
          </div>
        )}
      </div>
    </div>
  );
}
